import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from "../../components/tailwind/button";
import { Search, Plus } from "lucide-react";
import { CampaignTable } from "../../components/whatsapp/campaigns/CampaignTable";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/tailwind/dialog";
import ConfirmDialog from "../../components/tailwind/ConfirmDialog";
import { Input } from "../../components/tailwind/input";
import { Label } from "../../components/tailwind/label";
import {
  fetchCampaignsRequest,
  setFilter,
  setSearchQuery,
  cancelCampaignRequest,
  updateCampaignScheduleRequest,
} from "../../store/slices/campaignsSlice";
import { usePageTitle } from "../../hooks/usePageTitle";
import { EstimateCreditCostButton } from "../../components/whatsapp/billing/wallet/EstimateCreditCostButton";
import { showAlert } from "../../store/alertSlice";
import { whatsappPageShell, waShell } from "./utils/whatsappPageShell";
import { waBtn } from "./utils/whatsappUiTokens";
import { WhatsAppPageIntro } from "./components/WhatsAppPageIntro";
import { WaTableSkeleton } from "./components/skeletons/WhatsAppPageSkeletons";
import { cn } from "../../lib/utils";

export default function Campaigns() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  usePageTitle('Campaigns');

  const { list, filter, searchQuery, loading } = useSelector((state) => state.campaigns);
  const [rescheduleData, setRescheduleData] = useState({ isOpen: false, campaign: null, date: '', time: '' });
  const [viewDetailsData, setViewDetailsData] = useState({ isOpen: false, campaign: null });
  const [campaignToCancel, setCampaignToCancel] = useState(null);

  useEffect(() => {
    dispatch(fetchCampaignsRequest());
  }, [dispatch]);

  // Filter logic
  const filteredList = (list || []).filter(item => {
    let status = (item.status || '').toLowerCase();
    if (status === 'running') {
      status = 'completed';
    }
    const matchesFilter = filter === 'all' || status === filter;
    const matchesSearch = (item.name || '').toLowerCase().includes((searchQuery || '').toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleRowClick = (campaign) => {
    const status = campaign?.status?.toLowerCase();
    if (status === 'failed') {
      dispatch(showAlert({ message: 'This campaign has failed and no analytics are available.', type: 'error' }));
      return;
    }
    if (status === 'cancelled') {
      dispatch(showAlert({ message: 'This campaign has been cancelled and no analytics are available.', type: 'error' }));
      return;
    }
    navigate(`/whatsapp/campaigns/${campaign.campaign_id}`);
  };

  const handleViewDetails = (campaign) => {
    setViewDetailsData({ isOpen: true, campaign });
  };

  const handleCancel = (campaign) => {
    setCampaignToCancel(campaign);
  };

  const handleConfirmCancelCampaign = () => {
    if (!campaignToCancel) return;
    dispatch(cancelCampaignRequest({ campaignId: campaignToCancel.campaign_id }));
    setCampaignToCancel(null);
  };

  const handleReschedule = (campaign) => {
    setRescheduleData({
      isOpen: true,
      campaign: campaign,
      date: '',
      time: ''
    });
  };

  const confirmReschedule = () => {
    const { campaign, date, time } = rescheduleData;
    if (campaign && date && time) {
      const timestamp = new Date(`${date}T${time}`).getTime();
      if (!isNaN(timestamp)) {
        dispatch(updateCampaignScheduleRequest({
          campaignId: campaign.campaign_id,
          scheduledAt: timestamp
        }));
        setRescheduleData({ isOpen: false, campaign: null, date: '', time: '' });
      } else {
        alert("Invalid date or time");
      }
    }
  };

  return (
    <div className={waShell(whatsappPageShell.fluid, "animate-fade-in")}>
      <WhatsAppPageIntro
        description="Create, schedule, and track bulk WhatsApp campaigns."
        className="mb-4"
      />
      
      {/* 1. Actions Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 animate-fade-in-up">
        <div className="flex items-center gap-3">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input 
              placeholder="Search campaigns..." 
              className="pl-9 w-full h-10 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-slate-800 dark:bg-slate-900 dark:text-white" 
              value={searchQuery}
              onChange={(e) => dispatch(setSearchQuery(e.target.value))}
            />
          </div>
          
          {/* Custom Tabs */}
          <div className="flex p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
            {['all', 'completed', 'scheduled', 'cancelled'].map((tab) => (
              <button
                key={tab}
                onClick={() => dispatch(setFilter(tab))}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all capitalize ${
                  filter === tab 
                    ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white' 
                    : 'text-slate-500 hover:text-slate-900 dark:text-slate-400'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <EstimateCreditCostButton className={cn(waBtn.secondary, "h-10")} />
          <Button
            className={cn(waBtn.primary, "gap-2")}
            onClick={() => navigate('/whatsapp/campaigns/new')}
          >
            <Plus className="h-4 w-4" />
            New Campaign
          </Button>
        </div>
      </div>

      <Dialog open={rescheduleData.isOpen} onOpenChange={(open) => !open && setRescheduleData({ isOpen: false, campaign: null, date: '', time: '' })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Reschedule Campaign</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="reschedule-date">Date <span className="text-red-500">*</span></Label>
              <Input
                id="reschedule-date"
                type="date"
                value={rescheduleData.date}
                onChange={(e) => setRescheduleData(prev => ({ ...prev, date: e.target.value }))}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="reschedule-time">Time <span className="text-red-500">*</span></Label>
              <Input
                id="reschedule-time"
                type="time"
                value={rescheduleData.time}
                onChange={(e) => setRescheduleData(prev => ({ ...prev, time: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleData({ isOpen: false, campaign: null, date: '', time: '' })}>
              Cancel
            </Button>
            <Button 
              className="bg-[#1f2937] hover:bg-[#374151] text-white" 
              onClick={confirmReschedule}
              disabled={!rescheduleData.date || !rescheduleData.time}
            >
              Update Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={viewDetailsData.isOpen} onOpenChange={(open) => !open && setViewDetailsData({ isOpen: false, campaign: null })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Campaign Details</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {viewDetailsData.campaign && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</h4>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{viewDetailsData.campaign.name}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</h4>
                  <p className="text-sm text-gray-900 dark:text-gray-100 capitalize">{viewDetailsData.campaign.status?.toLowerCase() || 'N/A'}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Created At</h4>
                  <p className="text-sm text-gray-900 dark:text-gray-100">
                    {viewDetailsData.campaign.created_at 
                      ? new Date(viewDetailsData.campaign.created_at).toLocaleString()
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Scheduled For</h4>
                  <p className="text-sm text-gray-900 dark:text-gray-100">
                    {viewDetailsData.campaign.scheduled_at 
                      ? new Date(viewDetailsData.campaign.scheduled_at).toLocaleString()
                      : "Not Scheduled"}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Audience</h4>
                  <p className="text-sm text-gray-900 dark:text-gray-100 truncate" title={viewDetailsData.campaign.audience}>
                    {viewDetailsData.campaign.audience ? "CSV / Excel Source" : "Manual Entry"}
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDetailsData({ isOpen: false, campaign: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(campaignToCancel)}
        onOpenChange={(open) => {
          if (!open) setCampaignToCancel(null);
        }}
        title="Cancel campaign"
        description={
          <>
            Are you sure you want to cancel{" "}
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              &quot;{campaignToCancel?.name || "this campaign"}&quot;
            </span>
            ?
          </>
        }
        confirmLabel="Cancel campaign"
        onCancel={() => setCampaignToCancel(null)}
        onConfirm={handleConfirmCancelCampaign}
      />

      {loading ? (
        <WaTableSkeleton rows={6} cols={5} />
      ) : (
        <CampaignTable 
          campaigns={filteredList}
          onRowClick={handleRowClick}
          onCancel={handleCancel}
          onReschedule={handleReschedule}
          onView={handleViewDetails}
        />
      )}
    </div>
  );
}